import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import Page from '../../../../../components/Page';
import Breadcrumb from '../../../../../components/Breadcrumb';
import PlateCropEditor, { CropPreview, PlateLightbox, plateUrl } from '../../../../../components/data-management/PlateCropEditor';
import client from '../../../../../lib/feathers';

// One grape: its details and copy, its images and their crops, the names that
// point at it, and publishing. A grape page is public only once published, and
// publishing asks for the minimum a page needs (the checklist below).
// Edits are PATCHes: PUT is disallowed on these services, because
// feathers-sequelize PUT nulls every column absent from the payload.

const COLOURS = [['noir', 'Noir (black)'], ['blanc', 'Blanc (white)'], ['rouge', 'Rouge (red)'], ['rose', 'Rose (pink)'], ['gris', 'Gris (grey)']];
const STYLE_LABEL = 'Illustration generated in the style of the Ampélographie plates';
const KIND_LABEL = { historic_plate: 'Historic plate', photo: 'Photo', style_generated: 'Generated', original_art: 'Original art' };
const INPUT = 'mt-1 block w-full rounded-md border-0 py-1.5 px-3 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary';

export default function GrapeEdit() {
  const { gid } = useParams();
  const [grape, setGrape] = useState(null);
  const [form, setForm] = useState({});
  const [families, setFamilies] = useState([]);
  const [images, setImages] = useState([]);
  const [names, setNames] = useState([]);
  const [cropping, setCropping] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadImages = useCallback(async () => {
    const res = await client.service('admin/grape-image').find({ query: { grapeId: gid, $sort: { sortOrder: 1 }, $limit: 100 } });
    setImages(res.data || res);
  }, [gid]);

  useEffect(() => {
    (async () => {
      try {
        const g = await client.service('admin/grape').get(gid);
        setGrape(g);
        setForm({ name: g.name || '', color: g.color || '', familyId: g.familyId || '', vivcId: g.vivcId || '',
          originCountry: g.originCountry || '', summary: g.summary || '', description: g.description || '' });
        const fam = await client.service('admin/grape-family').find({ query: { $limit: 100, $sort: { name: 1 } } });
        setFamilies(fam.data || fam);
        const nm = await client.service('admin/varietal').find({ query: { grapeId: gid, $limit: 200, $sort: { name: 1 } } });
        setNames(nm.data || nm);
        await loadImages();
      } catch (e) {
        toast.error(e.message || 'Could not load the grape');
      }
    })();
  }, [gid, loadImages]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const data = { ...form, familyId: form.familyId || null, vivcId: form.vivcId ? Number(form.vivcId) : null,
        color: form.color || null, originCountry: form.originCountry || null, ...extra };
      const g = await client.service('admin/grape').patch(gid, data);
      setGrape(g);
      toast.success(extra.published === undefined ? 'Saved' : (g.published ? 'Published' : 'Unpublished'));
    } catch (e) {
      toast.error(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const patchImage = async (img, data) => {
    try {
      await client.service('admin/grape-image').patch(img.id, data);
      await loadImages();
    } catch (e) {
      toast.error(e.message || 'Could not update the image');
    }
  };

  const moveImage = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const a = images[i];
    const b = images[j];
    await client.service('admin/grape-image').patch(a.id, { sortOrder: j });
    await client.service('admin/grape-image').patch(b.id, { sortOrder: i });
    await loadImages();
  };

  if (!grape) return <Page title="Grape"><p className="text-sm text-gray-500">Loading...</p></Page>;

  const primary = images.find((i) => i.isPrimary);
  const checklist = [
    ['A summary is written', Boolean(form.summary && form.summary.trim())],
    ['A primary image is set', Boolean(primary)],
    ['Its crop is reviewed', Boolean(primary && primary.reviewed)],
  ];
  const ready = checklist.every(([, ok]) => ok);

  return (
    <Page title={grape.name}>
      <Toaster toastOptions={{ duration: 3000 }} />
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[{ name: 'Data Management', href: '/admin/data-management/grapes' },
          { name: 'Grapes', href: '/admin/data-management/grapes' }, { name: grape.name }]} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2 space-y-4 rounded-lg bg-white p-5 shadow">
          <h2 className="text-base font-semibold text-gray-900">Details and copy</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm text-gray-700">Name
              <input className={INPUT} value={form.name} onChange={set('name')} />
            </label>
            <label className="text-sm text-gray-700">Slug (fixed)
              <input className={`${INPUT} bg-gray-50 text-gray-500`} value={grape.slug} readOnly />
            </label>
            <label className="text-sm text-gray-700">Berry colour
              <select className={INPUT} value={form.color} onChange={set('color')}>
                <option value="">Unknown</option>
                {COLOURS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="text-sm text-gray-700">Family
              <select className={INPUT} value={form.familyId} onChange={set('familyId')}>
                <option value="">None</option>
                {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </label>
            <label className="text-sm text-gray-700">VIVC id
              <input className={INPUT} inputMode="numeric" value={form.vivcId} onChange={set('vivcId')} />
              {grape.vivcName ? <span className="text-xs text-gray-500">{`VIVC name: ${grape.vivcName}`}</span> : null}
            </label>
            <label className="text-sm text-gray-700">Origin country
              <input className={INPUT} value={form.originCountry} onChange={set('originCountry')} />
            </label>
          </div>
          <label className="block text-sm text-gray-700">Summary
            <textarea className={INPUT} rows={3} value={form.summary} onChange={set('summary')} />
            <span className={`text-xs ${form.summary.length > 160 ? 'text-amber-600' : 'text-gray-400'}`}>
              {`${form.summary.length} characters${form.summary.length > 160 ? ': long for a search result' : ''}`}
            </span>
          </label>
          <label className="block text-sm text-gray-700">Description
            <textarea className={INPUT} rows={8} value={form.description} onChange={set('description')} />
          </label>
          <button type="button" disabled={saving} onClick={() => save()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </section>

        <section className="space-y-4 rounded-lg bg-white p-5 shadow">
          <h2 className="text-base font-semibold text-gray-900">Publishing</h2>
          <p className="text-sm">
            {grape.published
              ? <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Published</span>
              : <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Draft: no public page</span>}
          </p>
          <ul className="space-y-1 text-sm">
            {checklist.map(([label, ok]) => (
              <li key={label} className={ok ? 'text-green-700' : 'text-gray-500'}>{`${ok ? 'Yes' : 'Not yet'}: ${label}`}</li>
            ))}
          </ul>
          {grape.published ? (
            <button type="button" disabled={saving} onClick={() => save({ published: false })}
              className="rounded-md px-4 py-2 text-sm text-gray-700 ring-1 ring-gray-300">Unpublish</button>
          ) : (
            <button type="button" disabled={saving || !ready} onClick={() => save({ published: true })}
              title={ready ? '' : 'Finish the checklist first'}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-40">Publish</button>
          )}

          <h2 className="pt-4 text-base font-semibold text-gray-900">{`Names (${names.length})`}</h2>
          <p className="text-xs text-gray-500">What people find on bottles. Historic names are kept for matching and never offered in the app.</p>
          <ul className="divide-y divide-gray-100 text-sm">
            {names.map((n) => (
              <li key={n.id} className="flex justify-between py-1">
                <span>{n.name}</span>
                <span className="text-xs text-gray-500">{`${n.kind}${n.offerInPicker ? '' : ', hidden'}`}</span>
              </li>
            ))}
          </ul>
          <Link to="/admin/data-management/varietals" className="text-sm text-primary">Edit names</Link>
        </section>
      </div>

      <section className="mt-6 rounded-lg bg-white p-5 shadow">
        <h2 className="text-base font-semibold text-gray-900">{`Images (${images.length})`}</h2>
        <p className="mb-4 text-xs text-gray-500">
          Each card is shown as the app will show it: the crop, fitted into a 3:4 frame. The full plate is one tap away there.
        </p>
        {!images.length ? <p className="text-sm text-gray-500">No images yet.</p> : null}
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {images.map((img, i) => {
            const att = img.attachment || {};
            const src = plateUrl(att.path, 600);
            const natural = att.width && att.height ? { w: att.width, h: att.height } : null;
            const box = att.cropBox || { x: 0, y: 0, w: 1, h: 1 };
            return (
              <div key={img.id} className="flex gap-3 rounded-md p-3 ring-1 ring-gray-200">
                <button type="button" onClick={() => setViewing(img)} title="See the full plate"
                  className="shrink-0 cursor-zoom-in self-start">
                  {natural ? <CropPreview src={src} box={box} natural={natural} width={120} />
                    : <img src={src} alt="" className="h-40 w-[120px] object-contain" />}
                </button>
                <div className="min-w-0 flex-1 space-y-1 text-sm">
                  <div className="font-medium text-gray-900">{img.captionName || '(no caption)'}</div>
                  <div className="flex flex-wrap gap-1">
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">{KIND_LABEL[img.kind] || img.kind}</span>
                    {img.isPrimary ? <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">Primary</span> : null}
                    {img.reviewed ? <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">Reviewed</span> : null}
                  </div>
                  {img.kind === 'style_generated' ? <p className="text-xs italic text-amber-700">{STYLE_LABEL}</p> : null}
                  <p className="text-xs text-gray-500">{img.credit}</p>
                  {img.sourceUrl ? <a href={img.sourceUrl} target="_blank" rel="noreferrer" className="text-xs text-primary">Source</a> : null}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button type="button" onClick={() => setViewing(img)} className="text-xs text-primary">View full plate</button>
                    <button type="button" onClick={() => setCropping(img)} className="text-xs text-primary">Crop</button>
                    {!img.isPrimary ? <button type="button" onClick={() => patchImage(img, { isPrimary: true })} className="text-xs text-primary">Make primary</button> : null}
                    <button type="button" onClick={() => patchImage(img, { reviewed: !img.reviewed })} className="text-xs text-gray-600">
                      {img.reviewed ? 'Mark unreviewed' : 'Mark reviewed'}
                    </button>
                    <button type="button" disabled={i === 0} onClick={() => moveImage(i, -1)} className="text-xs text-gray-600 disabled:opacity-30">Up</button>
                    <button type="button" disabled={i === images.length - 1} onClick={() => moveImage(i, 1)} className="text-xs text-gray-600 disabled:opacity-30">Down</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {viewing ? <PlateLightbox image={viewing} onClose={() => setViewing(null)} /> : null}
      {cropping ? (
        <PlateCropEditor image={cropping} onClose={() => setCropping(null)}
          onSaved={async () => { setCropping(null); await loadImages(); }} />
      ) : null}
    </Page>
  );
}
